import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, Send, ChevronDown } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  FetchAdminInquiryMessages,
  SendAdminInquiryMessage,
  UpdateAdminInquiryStatus,
  FetchAllAdminInquiries,
  ClearAdminInquiryAction,
} from "../../Store/Actions/AdminInquiryActions";

const STATUS_STYLES = {
  OPEN: "bg-green-100 text-green-700",
  IN_PROGRESS: "bg-yellow-100 text-yellow-700",
  CLOSED: "bg-gray-100 text-gray-600",
};

const STATUS_LABELS = {
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  CLOSED: "Closed",
};

const STATUS_OPTIONS = ["OPEN", "IN_PROGRESS", "CLOSED"];

function formatTime(isoString) {
  if (!isoString) return "";
  return new Date(isoString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const Spinner = () => (
  <div className="flex justify-center items-center py-24">
    <Loader2 className="animate-spin text-themeColor" size={32} />
  </div>
);

const MainAdminInquiryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { messages, loader, inquiries, actionSuccess, actionError } = useSelector(
    (state) => state.adminInquiry
  );

  const inquiry = inquiries.find((inq) => inq.id === id) ?? null;

  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const actionHandled = useRef(false);
  const statusDropdownRef = useRef(null);

  useEffect(() => {
    dispatch(FetchAdminInquiryMessages(id));
    // If inquiries list isn't loaded yet, fetch it to get the inquiry metadata
    if (inquiries.length === 0) {
      dispatch(FetchAllAdminInquiries());
    }
    return () => dispatch(ClearAdminInquiryAction());
  }, [dispatch, id, inquiries.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (actionSuccess && !actionHandled.current) {
      actionHandled.current = true;
      if (actionSuccess.data && actionSuccess.data.message !== undefined) {
        // Send message success
        setSending(false);
        setReplyText("");
      } else if (actionSuccess.data && actionSuccess.data.status !== undefined) {
        // Status update success
        setUpdatingStatus(false);
        toast.success("Status updated.");
      }
      dispatch(ClearAdminInquiryAction());
    }
  }, [actionSuccess, dispatch]);

  useEffect(() => {
    if (actionError && !actionHandled.current) {
      actionHandled.current = true;
      toast.error(actionError.message || "An error occurred.");
      setSending(false);
      setUpdatingStatus(false);
      dispatch(ClearAdminInquiryAction());
    }
  }, [actionError, dispatch]);

  // Close status dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function handleSend(e) {
    e.preventDefault();
    if (!replyText.trim()) return;
    actionHandled.current = false;
    setSending(true);
    dispatch(SendAdminInquiryMessage(id, replyText.trim()));
  }

  function handleStatusChange(newStatus) {
    setStatusDropdownOpen(false);
    if (newStatus === inquiry?.status) return;
    actionHandled.current = false;
    setUpdatingStatus(true);
    dispatch(UpdateAdminInquiryStatus(id, newStatus));
  }

  const currentStatus = inquiry?.status ?? "OPEN";

  return (
    <div className="p-6 flex flex-col h-full">
      {/* Back + Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/dashboard/admin-inquiries")}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 text-sm font-popinsMedium mb-4 transition"
        >
          <ArrowLeft size={16} />
          Back to Inquiries
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <h1 className="text-xl font-popinsBold text-gray-800">
              {inquiry?.subject ?? "Inquiry"}
            </h1>
            {inquiry && (
              <p className="text-sm font-popinsRegular text-[#61758A] mt-0.5">
                From:{" "}
                <span className="font-popinsMedium text-gray-700">
                  {inquiry.organizationName ||
                    [inquiry.senderFirstName, inquiry.senderLastName]
                      .filter(Boolean)
                      .join(" ") ||
                    "—"}
                </span>
              </p>
            )}
          </div>

          {/* Status Control */}
          <div className="relative flex items-center gap-2" ref={statusDropdownRef}>
            <span
              className={`inline-block text-xs font-popinsMedium px-3 py-1 rounded-full ${
                STATUS_STYLES[currentStatus] ?? "bg-gray-100 text-gray-600"
              }`}
            >
              {STATUS_LABELS[currentStatus] ?? currentStatus}
            </span>
            <button
              onClick={() => setStatusDropdownOpen((o) => !o)}
              disabled={updatingStatus}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-popinsMedium text-gray-600 hover:border-themeColor hover:text-themeColor transition disabled:opacity-50"
            >
              {updatingStatus ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <>
                  Change Status
                  <ChevronDown size={14} />
                </>
              )}
            </button>
            {statusDropdownOpen && (
              <div className="absolute right-0 top-10 z-20 bg-white border border-gray-100 rounded-xl shadow-lg w-40 py-1">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => handleStatusChange(opt)}
                    className={`w-full text-left px-4 py-2 text-sm font-popinsRegular hover:bg-gray-50 transition ${
                      currentStatus === opt ? "font-popinsMedium text-themeColor" : "text-gray-700"
                    }`}
                  >
                    {STATUS_LABELS[opt]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      {loader && messages.length === 0 ? (
        <Spinner />
      ) : (
        <div className="flex-1 overflow-y-auto space-y-4 mb-6 max-h-[calc(100vh-360px)] pr-1">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.isMine ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-3 shadow-sm ${
                  msg.isMine
                    ? "bg-themeColor text-white rounded-br-sm"
                    : "bg-white border border-gray-100 text-gray-800 rounded-bl-sm"
                }`}
              >
                {!msg.isMine && (
                  <p className="text-xs font-popinsSemiBold mb-1 text-themeColor">
                    {[msg.senderFirstName, msg.senderLastName]
                      .filter(Boolean)
                      .join(" ") || "User"}
                  </p>
                )}
                <p className="text-sm font-popinsRegular leading-relaxed whitespace-pre-wrap">
                  {msg.message}
                </p>
                <p
                  className={`text-xs mt-1.5 ${
                    msg.isMine ? "text-white/70" : "text-gray-400"
                  }`}
                >
                  {formatTime(msg.createdAt)}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Reply Box */}
      {currentStatus === "CLOSED" ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-5 py-3 text-sm font-popinsRegular text-gray-500 text-center">
          This inquiry is closed. Change the status to reply.
        </div>
      ) : (
        <form onSubmit={handleSend} className="flex gap-3 items-end">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            placeholder="Type your reply… (Enter to send, Shift+Enter for new line)"
            rows={2}
            className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm font-popinsRegular focus:outline-none focus:ring-2 focus:ring-themeColor resize-none"
          />
          <button
            type="submit"
            disabled={sending || !replyText.trim()}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-themeColor text-white hover:bg-themeColor/90 transition disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 mb-0.5"
          >
            {sending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default MainAdminInquiryDetail;
